import { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { Download, Loader2, ShieldCheck } from 'lucide-react';

/**
 * UK GDPR Article 20 — right to data portability.
 *
 * Calls fn_export_my_data(), which is SECURITY INVOKER, so RLS decides what the
 * caller can see. There is no org id to pass: the function derives it from the
 * caller's own profile, so this cannot be pointed at another tenant.
 *
 * The file is built client-side from the RPC's JSON and downloaded via an
 * object URL — no round trip through storage, so no copy of the user's data is
 * left sitting in a bucket afterwards.
 */
export const DataExportSection = () => {
    const [exporting, setExporting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [lastExport, setLastExport] = useState<string | null>(null);

    const handleExport = async () => {
        setExporting(true);
        setError(null);
        let url: string | null = null;

        try {
            const { data, error: rpcError } = await supabase.rpc('fn_export_my_data');
            if (rpcError) throw rpcError;
            if (!data) throw new Error('Export returned no data.');

            const blob = new Blob([JSON.stringify(data, null, 2)], {
                type: 'application/json',
            });
            url = URL.createObjectURL(blob);

            const stamp = new Date().toISOString().slice(0, 10);
            const link = document.createElement('a');
            link.href = url;
            link.download = `forgedash-data-export-${stamp}.json`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setLastExport(new Date().toLocaleString('en-GB'));
        } catch (err) {
            console.error('Data export failed:', err);
            setError(err instanceof Error ? err.message : 'Export failed. Please try again.');
        } finally {
            // Always release the object URL, including on the error path, so a
            // failed export can't leak the blob for the life of the document.
            if (url) URL.revokeObjectURL(url);
            setExporting(false);
        }
    };

    return (
        <div className="space-y-8 animate-in fade-in slide-in-from-bottom-2 duration-300">
            <div>
                <h3 className="text-xl font-bold text-fg flex items-center gap-2">
                    <ShieldCheck className="w-5 h-5 text-accent" />
                    Your Data
                </h3>
                <p className="text-sm text-fg-2">
                    Download everything we hold for your workspace.
                </p>
            </div>

            <div className="bg-panel border border-line rounded-[var(--fds-radius)] p-6 space-y-4">
                <div>
                    <h4 className="font-bold text-fg">Export workspace data</h4>
                    <p className="text-sm text-fg-2">
                        A machine-readable JSON file containing your profile, business details, team,
                        customers, vehicles, jobs, invoices, quotes, appointments and full ledger.
                        Provided under your right to data portability (UK GDPR Article 20).
                    </p>
                </div>

                <button
                    onClick={handleExport}
                    disabled={exporting}
                    className="bg-accent/15 hover:bg-accent/25 disabled:opacity-50 disabled:cursor-not-allowed text-accent px-4 py-2 rounded-[var(--fds-radius)] font-medium transition-colors flex items-center gap-2"
                >
                    {exporting ? (
                        <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Preparing your export…
                        </>
                    ) : (
                        <>
                            <Download className="w-4 h-4" />
                            Download my data
                        </>
                    )}
                </button>

                {lastExport && !error && (
                    <p className="text-sm text-fg-2">
                        Last exported {lastExport}.
                    </p>
                )}

                {error && (
                    <p className="text-sm text-danger">{error}</p>
                )}

                <p className="text-xs text-fg-2 pt-2 border-t border-line">
                    Bank details are excluded — they are held encrypted and are available through
                    your payment settings. Each section is capped at 10,000 rows; if you reach that
                    cap, contact support for a complete extract.
                </p>
            </div>
        </div>
    );
};
